import { useState } from 'react';

export type FATCATaxResidencyEntry = {
  countryValue: string | null | undefined;
  tinCode: string | null | undefined;
  id: string;
} & (
  | {
      tinAbsent: false;
    }
  | {
      tinAbsent: true;
      reasonList: { label: string; value: string }[];
      tinAbsentSelectedReason: string | null;
    }
  | {
      tinAbsent: true;
      tinAbsentTextReasonRequired: true;
      tinAbsentTextReason: string | null;
    }
);

export const useFATCATaxResidencies = () => {
  const [fatcaTaxResidencies, setFatcaTaxResidencies] = useState<
    FATCATaxResidencyEntry[]
  >([]);

  const addFATCATaxResidency = (): void => {
    setFatcaTaxResidencies((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        countryValue: null,
        tinCode: null,
        tinAbsent: false,
      },
    ]);
  };

  const setAllFATCATaxResidencies = (
    entries: FATCATaxResidencyEntry[],
  ): void => {
    setFatcaTaxResidencies(entries);
  };

  const removeFATCATaxResidency = (id: string): void => {
    setFatcaTaxResidencies((prev) => prev.filter((entry) => entry.id !== id));
  };

  const updateFATCATaxResidency = (
    id: string,
    updatedEntry: Partial<FATCATaxResidencyEntry>,
  ): void => {
    setFatcaTaxResidencies((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;

        // Determine the next value for tinAbsent
        const nextTinAbsent =
          updatedEntry.tinAbsent !== undefined
            ? updatedEntry.tinAbsent
            : entry.tinAbsent;

        if (nextTinAbsent === true) {
          // Ensure all required fields for tinAbsent: true
          return {
            ...entry,
            ...updatedEntry,
            tinAbsent: true,
            reasonList:
              (updatedEntry as any).reasonList ??
              (entry as any).reasonList ??
              [],
            tinAbsentSelectedReason:
              (updatedEntry as any).tinAbsentSelectedReason ??
              (entry as any).tinAbsentSelectedReason ??
              null,
          } as FATCATaxResidencyEntry;
        } else {
          // Only keep fields for tinAbsent: false
          return {
            ...entry,
            ...updatedEntry,
            tinAbsent: false,
          } as FATCATaxResidencyEntry;
        }
      }),
    );
  };

  return {
    setAllFATCATaxResidencies,
    addFATCATaxResidency,
    removeFATCATaxResidency,
    updateFATCATaxResidency,
    fatcaTaxResidencies,
  };
};
